import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";
import { ArrowRight, Shield, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const { logo_url, organization_name } = useBranding();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [needsMfa, setNeedsMfa] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { api } = await import("@/lib/api");
      const { data } = await api.post("/auth/login", { email, password, otp: otp || undefined });
      if (data.token) localStorage.setItem("icos_token", data.token);
      window.location.href = "/";
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail === "MFA_REQUIRED") { setNeedsMfa(true); setErr("Enter your 6-digit code."); }
      else setErr(typeof detail === "string" ? detail : e.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-white">
      {/* Left panel (imagery + statement) */}
      <div className="relative hidden lg:block bg-slate-950 text-white overflow-hidden">
        <img
          src="https://images.pexels.com/photos/17729218/pexels-photo-17729218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-40"
        />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-3">
              <img src={logo_url} alt={organization_name} className="w-12 h-12 object-contain bg-white p-1" />
              <div>
                <div className="font-heading font-extrabold text-2xl leading-none">ICOS</div>
                <div className="text-[10px] uppercase tracking-[0.3em] mt-1 text-slate-300">{organization_name} · Core Operating System</div>
              </div>
            </div>
          </div>
          <div className="max-w-md">
            <div className="overline text-slate-300 mb-6">Institutional Platform</div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl leading-[1.05] tracking-tight">
              One system, <br/>every case, <br/>every stakeholder.
            </h1>
            <p className="mt-6 text-slate-200 leading-relaxed max-w-sm">
              Identity, case management, referrals, and analytics — unified for
              executives, partners, government, and field workers.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-0 border-t border-white/20 pt-6 text-xs">
            <div className="border-r border-white/20 pr-4">
              <div className="overline text-slate-300">Secure</div>
              <div className="mt-1 font-heading font-extrabold text-lg">AES-256</div>
            </div>
            <div className="border-r border-white/20 pr-4 pl-4">
              <div className="overline text-slate-300">Roles</div>
              <div className="mt-1 font-heading font-extrabold text-lg">9 tiers</div>
            </div>
            <div className="pl-4">
              <div className="overline text-slate-300">Modules</div>
              <div className="mt-1 font-heading font-extrabold text-lg">9 core</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right panel (form) */}
      <div className="flex items-center justify-center p-8 lg:p-16 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <img src={logo_url} alt={organization_name} className="w-14 h-14 object-contain" />
            <div>
              <div className="font-heading font-extrabold text-2xl leading-none">ICOS</div>
              <div className="overline text-[9px] mt-1">{organization_name}</div>
            </div>
          </div>
          <div className="overline mb-3">Restricted Access</div>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight">Sign in</h2>
          <p className="mt-2 text-sm text-slate-500">Authorized personnel only. All actions are audited.</p>

          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <label className="overline block mb-2">Email</label>
              <input
                data-testid="login-email"
                type="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                className="w-full border border-slate-300 px-4 py-3 bg-white focus:border-[#0033A0] focus:outline-none font-mono text-sm"
                placeholder="you@organization.org"
              />
            </div>
            <div>
              <label className="overline block mb-2">Password</label>
              <input
                data-testid="login-password"
                type="password" required
                value={password} onChange={e => setPassword(e.target.value)}
                className="w-full border border-slate-300 px-4 py-3 bg-white focus:border-[#0033A0] focus:outline-none font-mono text-sm"
                placeholder="••••••••"
              />
            </div>
            {needsMfa && (
              <div>
                <label className="overline block mb-2">Authenticator code</label>
                <input data-testid="login-otp" value={otp} onChange={e => setOtp(e.target.value)}
                  className="w-full border border-slate-300 px-4 py-3 focus:border-[#0033A0] focus:outline-none font-mono text-sm"
                  placeholder="123456" autoFocus />
              </div>
            )}
            {err && <div data-testid="login-error" className="border border-red-300 bg-red-50 text-red-700 text-sm px-4 py-3">{err}</div>}
            <button
              data-testid="login-submit"
              disabled={loading}
              type="submit"
              className="w-full bg-[#0033A0] text-white py-3 px-5 flex items-center justify-center gap-2 hover:bg-slate-900 transition-colors disabled:opacity-60"
            >
              <Lock size={16} /> {loading ? "Verifying…" : "Sign in securely"} <ArrowRight size={16} />
            </button>
          </form>

          <div className="mt-8 border-t border-slate-200 pt-6 text-xs text-slate-500">
            <div>New to ICOS? <Link data-testid="to-register" to="/register" className="text-[#0033A0] font-semibold underline underline-offset-2">Request an account</Link></div>
            <div className="mt-2 font-mono">Demo · admin@iscic.org / Admin@12345</div>
          </div>
        </div>
      </div>
    </div>
  );
}
